import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PALETTE } from '../tokens';
import { useAuth } from '../context/AuthContext';
import { rciApi } from '../api/rci';
import { insightsApi } from '../api/insights';
import { authApi } from '../api/auth';
import PaywallView from '../components/PaywallView';
import { presentedScore } from '../utils/presentedScore';
import { isNativeIOS } from '../utils/platform';
import { coherenceLabel } from '../utils/coherenceLabels';
import { DOMAIN_META } from '../utils/domainLabels';

/* ------------------------------------------------------------------ */
/* Design-system constants matching CoherenceDashboard                 */
/* ------------------------------------------------------------------ */

const STATUS_LABELS = {
  CALIBRATING: 'Calibrating',
  FRAGMENTED: 'Scattered',
  STABILIZING: 'Stabilizing',
  STABLE_COHERENCE: 'Strong Signal',
  REGRESSING: 'Shifting back',
};

function scoreToDots(score) {
  if (score == null || score < 0) return 1;
  if (score < 0.2) return 1;
  if (score < 0.4) return 2;
  if (score < 0.6) return 3;
  if (score < 0.8) return 4;
  return 5;
}

/* ------------------------------------------------------------------ */
/* Styles — scoped, CSS-only animation. No libraries.                 */
/* ------------------------------------------------------------------ */

const STYLES = `
  .rv-root {
    font-family: 'DM Sans', sans-serif;
    background: ${PALETTE.bg};
    min-height: 100vh;
    min-height: 100dvh;
    color: ${PALETTE.text};
    max-width: 420px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: max(48px, calc(env(safe-area-inset-top) + 12px)) 28px 64px;
    position: relative;
    overflow: hidden;
  }
  .serif { font-family: 'Playfair Display', Georgia, serif; }

  /* Phase fade-in — opacity + translateY, pure CSS transition */
  .rv-phase {
    opacity: 0;
    transform: translateY(16px);
    transition: opacity 600ms ease, transform 600ms ease;
  }
  .rv-phase.rv-visible {
    opacity: 1;
    transform: translateY(0);
  }
  /* Phase 3 (status label) uses a shorter 400ms transition */
  .rv-phase-fast {
    opacity: 0;
    transform: translateY(12px);
    transition: opacity 400ms ease, transform 400ms ease;
  }
  .rv-phase-fast.rv-visible {
    opacity: 1;
    transform: translateY(0);
  }

  /* CTA button */
  .rv-cta-btn {
    background: ${PALETTE.primary};
    color: ${PALETTE.bg};
    border: none;
    border-radius: 14px;
    padding: 15px 32px;
    font-size: 14px;
    font-weight: 500;
    font-family: 'DM Sans', sans-serif;
    cursor: pointer;
    letter-spacing: 0.01em;
    transition: opacity 0.15s;
    width: 100%;
    max-width: 280px;
  }
  .rv-cta-btn:hover { opacity: 0.85; }
  .rv-cta-btn:active { opacity: 0.72; }
  .rv-cta-btn:disabled { opacity: 0.5; cursor: default; }

  /* Ambient wash */
  .rv-wash {
    position: fixed;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 420px;
    height: 400px;
    pointer-events: none;
    z-index: 0;
    background: radial-gradient(ellipse at 50% 10%, rgba(158,132,86,0.10) 0%, transparent 72%);
  }

  /* Deferred-insight shimmer — a slow breathing line while the reveal insight
     finishes generating. Calm, not a spinner. */
  .rv-shimmer {
    background: linear-gradient(90deg,
      rgba(158,132,86,0.08) 0%, rgba(158,132,86,0.18) 50%, rgba(158,132,86,0.08) 100%);
    background-size: 200% 100%;
    animation: rvShimmer 1.8s ease-in-out infinite;
  }
  @keyframes rvShimmer {
    0% { background-position: 200% 0; opacity: 0.7; }
    50% { opacity: 1; }
    100% { background-position: -200% 0; opacity: 0.7; }
  }

  /* Pull-thread pills — tappable prompts that route into chat. */
  .rv-thread {
    display: block;
    width: 100%;
    text-align: left;
    background: ${PALETTE.surface};
    border: 1px solid ${PALETTE.border};
    border-radius: 14px;
    padding: 13px 16px;
    margin-bottom: 10px;
    font-family: 'DM Sans', sans-serif;
    font-size: 13.5px;
    font-weight: 400;
    color: ${PALETTE.text};
    cursor: pointer;
    transition: border-color 0.15s, transform 0.1s;
  }
  .rv-thread:hover { border-color: ${PALETTE.primary}; }
  .rv-thread:active { transform: scale(0.99); }
  .rv-thread-arrow { color: ${PALETTE.primaryMid}; float: right; }
`;

/* ------------------------------------------------------------------ */
/* RevealScreen                                                       */
/* ------------------------------------------------------------------ */

export default function RevealScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token, patchUser } = useAuth();

  /* ---- Data loading ---- */
  const [snapshot, setSnapshot] = useState(null);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  // The reveal insight (LLM-generated) can take a few seconds on first open;
  // it loads separately from the snapshot so phases 1-2 (framing + score) are
  // never blocked. The insight block holds a gentle shimmer until this resolves.
  const [insightsLoading, setInsightsLoading] = useState(true);
  const [dismissing, setDismissing] = useState(false);
  // Set true only when a non-entitled user dismisses the reveal. The
  // paywall renders AFTER the reveal moment, never instead of it.
  const [showPaywall, setShowPaywall] = useState(false);
  const [pollingSubscription, setPollingSubscription] = useState(false);
  const [pollAttempts, setPollAttempts] = useState(0);

  useEffect(() => {
    if (!user) return;
    // Snapshot — fast, never gated — unblocks the cinematic phases immediately.
    rciApi.snapshot(user.id, token).catch(() => null)
      .then((snap) => setSnapshot(snap))
      .finally(() => setLoading(false));
    // Insights (reveal insight + pull threads) load in parallel; the screen
    // shimmers the insight block rather than waiting on this.
    insightsApi.get(user.id, token).catch(() => null)
      .then((ins) => setInsights(ins))
      .finally(() => setInsightsLoading(false));
  }, [user, token]);

  /* ---- Phase timing ---- */
  const [visiblePhase, setVisiblePhase] = useState(0);

  useEffect(() => {
    if (loading) return;
    // Kick off phase 1 immediately
    const timers = [
      setTimeout(() => setVisiblePhase(1), 0),       // Phase 1: framing copy
      setTimeout(() => setVisiblePhase(2), 800),      // Phase 2: score
      setTimeout(() => setVisiblePhase(3), 1600),     // Phase 3: status label
      setTimeout(() => setVisiblePhase(4), 2400),     // Phase 4: insight
      setTimeout(() => setVisiblePhase(5), 3400),     // Phase 5: domains
      setTimeout(() => setVisiblePhase(6), 4200),     // Phase 6: CTA
    ];
    return () => timers.forEach(clearTimeout);
  }, [loading]);

  /* ---- Post-subscription polling ---- */
  useEffect(() => {
    if (!user || !pollingSubscription || pollAttempts >= 10) return;

    const pollTimer = setTimeout(async () => {
      try {
        const updated = await insightsApi.get(user.id, token).catch(() => null);
        if (updated && !updated.reveal_gated) {
          // Subscription confirmed, re-render
          setInsights(updated);
          setPollingSubscription(false);
          setPollAttempts(0);
          return;
        }
        setPollAttempts((prev) => prev + 1);
      } catch (err) {
        console.error('Polling error:', err);
        setPollAttempts((prev) => prev + 1);
      }
    }, 2000);

    return () => clearTimeout(pollTimer);
  }, [user, token, pollingSubscription, pollAttempts]);

  /* ---- Query params ---- */
  const subscribed = searchParams.get('subscribed') === 'true';
  const cancelled = searchParams.get('cancelled') === 'true';

  /* ---- Post-subscription flow ---- */
  useEffect(() => {
    if (subscribed && !pollingSubscription) {
      setPollingSubscription(true);
      setPollAttempts(0);
    }
  }, [subscribed, pollingSubscription]);

  /* ---- Derived data ---- */
  const coherenceStatus = snapshot?.coherence_status || null;
  const isSafetyGated = coherenceStatus === 'SAFETY_GATED';
  const score = snapshot ? presentedScore(snapshot) : null;
  const scoreAvailable = score !== null && score > 0;
  const statusLabel = coherenceLabel(coherenceStatus, { fallback: 'Fragmented' });

  const bioScore = snapshot?.bio_score ?? null;
  const psychScore = snapshot?.psych_score ?? null;
  const behScore = snapshot?.beh_score ?? null;
  const scores = { bio: bioScore, psych: psychScore, beh: behScore };

  const bioLabel = insights?.bio_label || null;
  const psychLabel = insights?.psych_label || null;
  const behLabel = insights?.beh_label || null;
  const labels = { bio_label: bioLabel, psych_label: psychLabel, beh_label: behLabel };

  const revealInsight = insights?.reveal_insight || null;
  const pullThreads = insights?.pull_threads || [];
  const revealGated = insights?.reveal_gated || false;
  // Archetype the user just met — passed to the paywall so its headline
  // continues the reveal moment ("You just met your Masked Executor.").
  // Null until determination runs / on failure → paywall falls back.
  const archetypeName = insights?.archetype_name || null;

  /* ---- SAFETY_GATED redirect ---- */
  useEffect(() => {
    if (!loading && isSafetyGated) {
      navigate('/coherence', { replace: true });
    }
  }, [loading, isSafetyGated, navigate]);

  /* ---- Pull-thread tap → chat, seeded mid-conversation ----
     The thread text becomes the user's first message; the reveal insight +
     this week's scores ride along as context so the chat opens grounded, not
     cold. The thread_tapped event is emitted server-side from the seeded
     chat call (context.source === 'reveal_thread'). */
  const handleThreadTap = useCallback((threadText) => {
    navigate('/chat', {
      state: {
        seedMessage: threadText,
        seedContext: {
          source: 'reveal_thread',
          thread_text: threadText,
          reveal_insight: revealInsight,
          coherence_status: coherenceStatus,
          rci_total: snapshot?.rci_total ?? null,
          bio_score: bioScore,
          psych_score: psychScore,
          beh_score: behScore,
        },
      },
    });
  }, [navigate, revealInsight, coherenceStatus, snapshot, bioScore, psychScore, behScore]);

  /* ---- Dismiss handler ---- */
  const handleDismiss = useCallback(async () => {
    if (dismissing) return;
    setDismissing(true);
    // Mark the reveal seen BEFORE branching. This is also what emits the
    // ``reveal_dismissed`` funnel event server-side (PATCH /auth/me), so
    // it fires at the end of the reveal moment — not at the paywall.
    try {
      await authApi.updateProfile({ has_seen_reveal: true }, token);
      patchUser({ has_seen_reveal: true });
    } catch {
      // Best-effort — still route the user so they aren't stranded.
    }
    // The reveal is the product moment; the paywall is the response to it.
    // Non-entitled users (reveal_gated) land on the paywall now that
    // they've experienced the reveal. Entitled users (founding_comp /
    // founding) drop straight into the full app.
    //
    // Land on Home (/dashboard), NOT /coherence. /coherence is the only
    // screen whose bottom nav carries a "Signal" tab (linking to itself);
    // every other screen's nav has "Domains" instead. Dismissing to
    // /coherence made that Signal tab flash in and then vanish the moment
    // the user tapped Home — the nav bug from the June 10 reveal. Home is
    // also the correct place to deposit a user who just finished the
    // reveal: the tab bar restores with HOME active.
    // The iOS App Store binary may never surface a purchase flow that
    // bypasses In-App Purchase (Guideline 3.1.1), so the native shell
    // skips the paywall entirely and lands on Home like an entitled user.
    if (revealGated && !isNativeIOS()) {
      setShowPaywall(true);
      setDismissing(false);
    } else {
      navigate('/dashboard', { replace: true });
    }
  }, [dismissing, token, patchUser, navigate, revealGated]);

  /* ---- Post-reveal paywall ---- */
  // Renders only after handleDismiss sets showPaywall — i.e. once the
  // non-entitled user has seen the full reveal and tapped Continue. It is
  // deliberately NOT gated on revealGated alone, so the paywall can never
  // substitute for the reveal the way it used to.
  if (showPaywall) {
    return <PaywallView archetype={archetypeName} />;
  }

  /* ---- Post-subscription polling message ---- */
  if (pollingSubscription && pollAttempts < 10) {
    return (
      <>
        <style>{STYLES}</style>
        <div className="rv-root">
          <div className="rv-wash" />
          <div style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
            <p
              style={{
                fontSize: 15,
                color: PALETTE.textMid,
                lineHeight: 1.72,
                fontWeight: 300,
                maxWidth: 280,
                margin: '0 auto',
              }}
            >
              Your founding membership is confirmed. Loading your reveal...
            </p>
          </div>
        </div>
      </>
    );
  }

  /* ---- Polling failed: show message ---- */
  if (pollingSubscription && pollAttempts >= 10) {
    return (
      <>
        <style>{STYLES}</style>
        <div className="rv-root">
          <div className="rv-wash" />
          <div style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
            <p
              style={{
                fontSize: 15,
                color: PALETTE.textMid,
                lineHeight: 1.72,
                fontWeight: 300,
                maxWidth: 280,
                margin: '0 auto 24px',
              }}
            >
              Your payment is processing — refresh in a moment.
            </p>
            <button
              className="rv-cta-btn"
              onClick={() => window.location.reload()}
              style={{ maxWidth: 200 }}
            >
              Refresh
            </button>
          </div>
        </div>
      </>
    );
  }

  /* ---- Loading state: blank screen, unhurried ---- */
  if (loading || isSafetyGated) {
    return (
      <>
        <style>{STYLES}</style>
        <div className="rv-root">
          <div className="rv-wash" />
        </div>
      </>
    );
  }

  /* ---- No-score / no-insight fallback ----
     The reveal must never render a silent hole. Task 1 makes the backend
     generate the insight on read (with a rule-based fallback if Haiku
     fails), so reveal_insight should always be present here. This is the
     last-resort net: if the score is missing OR the insight is still
     empty after load, route to the unhurried "still taking shape" screen
     rather than showing a reveal with a blank where the insight belongs.
     NOTE: we wait for insights to finish loading before treating a null
     insight as "no insight" — while it's still generating we shimmer (below),
     never fall through to this screen. */
  if (!scoreAvailable || (!insightsLoading && !revealInsight)) {
    return (
      <>
        <style>{STYLES}</style>
        <div className="rv-root">
          <div className="rv-wash" />
          <div style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
            <div className={`rv-phase ${visiblePhase >= 1 ? 'rv-visible' : ''}`}>
              <p
                style={{
                  fontSize: 15,
                  color: PALETTE.textMid,
                  lineHeight: 1.72,
                  fontWeight: 300,
                  maxWidth: 280,
                  margin: '0 auto 48px',
                }}
              >
                Your signal is still taking shape. Check back tomorrow.
              </p>
            </div>
            <div className={`rv-phase ${visiblePhase >= 2 ? 'rv-visible' : ''}`}>
              <button className="rv-cta-btn" onClick={handleDismiss} disabled={dismissing}>
                Continue
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  /* ---- Full reveal ---- */
  return (
    <>
      <style>{STYLES}</style>
      <div className="rv-root">
        <div className="rv-wash" />

        <div
          style={{
            position: 'relative',
            zIndex: 1,
            textAlign: 'center',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {/* Phase 1 — Framing headline. This is the emotional announcement
              of the whole reveal, so it carries headline weight (serif, large)
              — not a tiny context line. The score in phase 2 is still the
              centerpiece; this is hierarchy, not shouting. */}
          <div
            className={`rv-phase ${visiblePhase >= 1 ? 'rv-visible' : ''}`}
            style={{ marginBottom: 40 }}
          >
            <h1
              className="serif"
              style={{
                fontSize: 28,
                fontStyle: 'italic',
                fontWeight: 400,
                color: PALETTE.text,
                lineHeight: 1.32,
                letterSpacing: '-0.01em',
                maxWidth: 300,
                margin: '0 auto',
              }}
            >
              Seven days of signal. Here's what it shows.
            </h1>
          </div>

          {/* Phase 2 — Score (primary headline) */}
          <div
                className={`rv-phase ${visiblePhase >= 2 ? 'rv-visible' : ''}`}
                style={{ marginBottom: 4 }}
              >
                <div
                  className="serif"
                  style={{
                    fontSize: 72,
                    fontWeight: 400,
                    fontStyle: 'italic',
                    color: PALETTE.text,
                    lineHeight: 1,
                    letterSpacing: '-0.02em',
                  }}
                >
                  {score}
                </div>
              </div>

              {/* Coherence state label */}
              <div
                className={`rv-phase-fast ${visiblePhase >= 3 ? 'rv-visible' : ''}`}
                style={{ marginBottom: 32 }}
              >
                <div
                  style={{
                    fontSize: 10,
                    letterSpacing: '0.18em',
                    textTransform: 'uppercase',
                    color: PALETTE.primaryMid,
                    fontWeight: 500,
                  }}
                >
                  {statusLabel}
                </div>
              </div>

              {/* Reveal insight. If it's still generating when phase 4
                  arrives, hold a gentle breathing shimmer in its place rather
                  than a blank — the eager warm-up usually means it's ready. */}
              {revealInsight ? (
                <div
                  className={`rv-phase ${visiblePhase >= 4 ? 'rv-visible' : ''}`}
                  style={{ marginBottom: 36 }}
                >
                  <p
                    style={{
                      fontSize: 14,
                      color: PALETTE.textMid,
                      lineHeight: 1.72,
                      fontWeight: 300,
                      maxWidth: 300,
                      margin: '0 auto',
                    }}
                  >
                    {revealInsight}
                  </p>
                </div>
              ) : (insightsLoading && visiblePhase >= 4 && (
                <div
                  className={`rv-phase ${visiblePhase >= 4 ? 'rv-visible' : ''}`}
                  style={{ marginBottom: 36, width: '100%', maxWidth: 300, margin: '0 auto 36px' }}
                  aria-label="Reading your week"
                >
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="rv-shimmer" style={{
                      height: 11, borderRadius: 6, margin: '0 auto 10px',
                      width: i === 2 ? '62%' : '100%',
                    }} />
                  ))}
                </div>
              ))}

          {/* Phase 5 — Domain rows */}
          <div
            className={`rv-phase ${visiblePhase >= 5 ? 'rv-visible' : ''}`}
            style={{ width: '100%', maxWidth: 340, marginBottom: 40 }}
          >
            <div
              style={{
                background: PALETTE.surface,
                borderRadius: 20,
                padding: '18px 22px',
                boxShadow: '0 1px 16px rgba(28,24,20,0.07)',
              }}
            >
              {DOMAIN_META.map((domain, idx) => {
                const dotCount = scoreToDots(scores[domain.key]);
                const label = labels[domain.labelField];
                /* Stagger: first at phase 5, then +200ms each */
                const rowDelay = idx * 200;
                return (
                  <div key={domain.key}>
                    <div
                      className={`rv-phase ${visiblePhase >= 5 ? 'rv-visible' : ''}`}
                      style={{
                        transitionDelay: visiblePhase >= 5 ? `${rowDelay}ms` : '0ms',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '12px 0',
                      }}
                    >
                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: 500,
                          color: PALETTE.text,
                          minWidth: 72,
                        }}
                      >
                        {domain.display}
                      </span>
                      <div
                        style={{
                          display: 'flex',
                          gap: 5,
                          flex: '0 0 auto',
                          margin: '0 16px',
                        }}
                      >
                        {[1, 2, 3, 4, 5].map((n) => (
                          <div
                            key={n}
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              background:
                                n <= dotCount
                                  ? PALETTE.primary
                                  : PALETTE.border,
                              transition: 'background 0.3s ease',
                            }}
                          />
                        ))}
                      </div>
                      <span
                        style={{
                          fontSize: 12,
                          color: PALETTE.textMid,
                          fontWeight: 300,
                          textAlign: 'right',
                          flex: 1,
                          minWidth: 0,
                          // Wrap rather than truncate — a clipped label
                          // ("Slipped below your ba…") tells the user nothing.
                          // Labels are short (≤22 chars), so this never
                          // exceeds two lines.
                          lineHeight: 1.28,
                          whiteSpace: 'normal',
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {label || '—'}
                      </span>
                    </div>
                    {idx < DOMAIN_META.length - 1 && (
                      <div style={{ height: 1, background: PALETTE.surfaceAlt }} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Phase 5.5 — Pull threads. The curiosity the reveal generates now
              has somewhere to go: tap a thread to open the chat mid-thought. */}
          {pullThreads.length > 0 && (
            <div
              className={`rv-phase ${visiblePhase >= 5 ? 'rv-visible' : ''}`}
              style={{ width: '100%', maxWidth: 340, marginBottom: 36 }}
            >
              <p style={{
                fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase',
                color: PALETTE.textMute, fontWeight: 500, marginBottom: 12, textAlign: 'left',
              }}>
                Pull a thread
              </p>
              {pullThreads.slice(0, 2).map((thread, i) => (
                <button
                  key={i}
                  className="rv-thread"
                  onClick={() => handleThreadTap(thread)}
                >
                  {thread}
                  <span className="rv-thread-arrow">→</span>
                </button>
              ))}
            </div>
          )}

          {/* CTA — fades in after all phases complete */}
          <div
            className={`rv-phase ${visiblePhase >= 6 ? 'rv-visible' : ''}`}
            style={{ width: '100%', maxWidth: 300 }}
          >
            <p
              style={{
                fontSize: 13,
                color: PALETTE.textMute,
                lineHeight: 1.72,
                fontWeight: 300,
                marginBottom: 20,
              }}
            >
              Come back tomorrow. The signal gets clearer over time.
            </p>
            <button
              className="rv-cta-btn"
              onClick={handleDismiss}
              disabled={dismissing}
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
